import type { AuthProvider } from '@refinedev/core'

import { apiClient, normalizeError, tokenStorage } from './api-client'

/**
 * Auth Provider：对接 Fastify 后端 JWT 登录
 *
 * 后端 API：
 *   POST /api/admin/login          { username, password } → { token, admin, expiresAt }
 *   GET  /api/admin/me             → { id, username, role }
 */

interface AdminInfo {
  id: string
  username: string
  role: string
}

interface LoginResponse {
  token: string
  admin: AdminInfo
  expiresAt: string
}

const ADMIN_KEY = 'license-admin-user'

/** 存储 admin 信息（用于 UI 展示，不敏感） */
function storeAdmin(admin: AdminInfo): void {
  localStorage.setItem(ADMIN_KEY, JSON.stringify(admin))
}

function getStoredAdmin(): AdminInfo | null {
  const raw = localStorage.getItem(ADMIN_KEY)
  if (!raw) return null
  try {
    return JSON.parse(raw) as AdminInfo
  } catch {
    return null
  }
}

export const authProvider: AuthProvider = {
  // ── 登录 ─────────────────────────────────────────────
  async login({ username, password }) {
    try {
      const { data } = await apiClient.post<LoginResponse>('/api/admin/login', {
        username,
        password
      })
      tokenStorage.set(data.token)
      storeAdmin(data.admin)
      return { success: true, redirectTo: '/' }
    } catch (err) {
      const normalized = normalizeError(err)
      return {
        success: false,
        error: new Error(normalized.message)
      }
    }
  },

  // ── 登出 ─────────────────────────────────────────────
  async logout() {
    tokenStorage.clear()
    localStorage.removeItem(ADMIN_KEY)
    return { success: true, redirectTo: '/login' }
  },

  // ── 校验登录态（每次路由切换时调用） ──────────────────
  async check() {
    const token = tokenStorage.get()
    if (!token) {
      return { authenticated: false, redirectTo: '/login', logout: true }
    }
    try {
      // 调用 /me 验证 token 有效性
      await apiClient.get('/api/admin/me')
      return { authenticated: true }
    } catch {
      tokenStorage.clear()
      localStorage.removeItem(ADMIN_KEY)
      return { authenticated: false, redirectTo: '/login', logout: true }
    }
  },

  // ── 解析错误：401 时触发登出 ─────────────────────────
  async onError(error) {
    const normalized = normalizeError(error)
    if (normalized.statusCode === 401) {
      tokenStorage.clear()
      localStorage.removeItem(ADMIN_KEY)
      return { logout: true, redirectTo: '/login' }
    }
    return {}
  },

  // ── 获取当前管理员信息（用于 UI 展示） ──────────────
  async getIdentity() {
    const stored = getStoredAdmin()
    if (stored) return stored
    try {
      const { data } = await apiClient.get<AdminInfo>('/api/admin/me')
      storeAdmin(data)
      return data
    } catch {
      return null
    }
  }
}
